/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { useClientCrudStore } from "../../context/ClientManagementContext";
import type { ClientRow, ClientContact, LeadOrigin, LeadInteraction } from "../../context/ClientManagementContext";
import { useSocialUpdates } from "../../context/SocialUpdatesContext";
import backendClientsCrud from "@services/housien/clients";
import { useUrlFilters } from "@/shared/hooks/use-url-filters";
import { extractClientDetailExtras } from "@/shared/utils/extract-client-detail-extras";
import {
  createInfiniteScrollGate,
  markNextPageRequested,
  resetInfiniteScrollGate,
  shouldRequestNextPage,
} from "@/shared/utils/infinite-scroll-gate";

/**
 * Whether an email is a synthetic placeholder generated for Meta leads.
 *
 * @param email - The email address to inspect.
 * @returns True when the email uses the synthetic Meta-lead domain.
 */
function isSyntheticEmail(email: string): boolean {
  return email.endsWith("@meta-lead.local");
}

/**
 * Extracts a structured lead origin from raw client preferences.
 *
 * @param prefs - The raw preferences record.
 * @returns The parsed lead origin, or undefined when absent/invalid.
 */
function parseLeadOrigin(prefs: Record<string, unknown>): LeadOrigin | undefined {
  const raw = prefs.lead_origin;
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  const socialAccounts = Array.isArray(r.social_accounts)
    ? (r.social_accounts as Record<string, unknown>[])
        .filter((a) => a.platform && a.external_user_id)
        .map((a) => ({
          platform: String(a.platform),
          external_user_id: String(a.external_user_id),
        }))
    : undefined;
  return {
    platform: String(r.platform ?? ""),
    source: String(r.source ?? ""),
    external_user_id: strOrUndef(r.external_user_id),
    first_captured_at: strOrUndef(r.first_captured_at),
    social_accounts: socialAccounts,
  };
}

/**
 * Coerces a value to a trimmed string, or undefined when falsy.
 *
 * @param v - The raw value.
 * @returns The stringified value, or undefined when falsy.
 */
const strOrUndef = (v: unknown): string | undefined => (v ? String(v) : undefined);

/**
 * Maps a raw interaction item into a typed lead interaction.
 *
 * @param item - The raw interaction value.
 * @returns The normalized lead interaction.
 */
function toLeadInteraction(item: unknown): LeadInteraction {
  const i = (item ?? {}) as Record<string, unknown>;
  return {
    type: String(i.type ?? ""),
    text: strOrUndef(i.text),
    reaction_type: strOrUndef(i.reaction_type),
    property_id: strOrUndef(i.property_id),
    publication_id: strOrUndef(i.publication_id),
    permalink: strOrUndef(i.permalink),
    captured_at: String(i.captured_at ?? ""),
    platform: strOrUndef(i.platform),
  };
}

/**
 * Extracts the array of lead interactions from raw client preferences.
 *
 * @param prefs - The raw preferences record.
 * @returns The parsed interactions, or undefined when absent/invalid.
 */
function parseLeadInteractions(prefs: Record<string, unknown>): LeadInteraction[] | undefined {
  const raw = prefs.lead_interactions;
  if (!Array.isArray(raw)) return undefined;
  return raw.map(toLeadInteraction);
}

/**
 * Coerces an empty string to undefined; keeps non-empty strings.
 *
 * @param v - Possibly empty string value
 * @returns The value, or undefined when empty/absent
 */
function emptyToUndef(v: string | undefined): string | undefined {
  if (!v) return undefined;
  return v;
}

/**
 * Maps a raw backend client record (nested model) into a typed client row.
 *
 * @param c - The raw client record from the backend.
 * @returns The normalized client row.
 */
function toClientRow(c: any): ClientRow {
  const metadata = c.metadata ?? {};
  const personalInfo = c.personal_info ?? {};
  const identity = c.identity ?? {};
  const assignment = c.assignment ?? {};
  const contacts: ClientContact[] = assignment.contacts ?? [];
  const demandProfile = c.demand_profile ?? {};
  const extras = extractClientDetailExtras(c as Record<string, unknown>);

  const rawEmail = contacts.find((ct: { type: string }) => ct.type === 'email')?.value ?? '';
  const displayEmail = isSyntheticEmail(rawEmail) ? '' : rawEmail;
  const phone = contacts.find((ct: { type: string }) => ct.type === 'phone')?.value
    ?? contacts.find((ct: { type: string }) => ct.type === 'whatsapp')?.value
    ?? '';

  return {
    client_id: metadata.client_id ?? c.client_id ?? '',
    full_name: personalInfo.full_name ?? identity.corporate_name ?? '',
    email: displayEmail,
    phone,
    status: metadata.status ?? 'lead',
    primary_broker: assignment.primary_broker ?? undefined,
    type_of_client: identity.person_type ?? 'individual',
    last_contact_date: metadata.last_contact_date ?? '',
    preferences: demandProfile,
    owned_properties_count: Array.isArray(
      (c.supply_profile as { owned_properties?: unknown[] } | undefined)?.owned_properties,
    )
      ? ((c.supply_profile as { owned_properties: unknown[] }).owned_properties).length
      : 0,
    cpf: identity.cpf ?? '',
    cnpj: identity.cnpj ?? '',
    birth_date: personalInfo.birth_date ?? '',
    nationality: emptyToUndef(extras.nationality),
    profession: emptyToUndef(extras.profession),
    marital_status: emptyToUndef(extras.marital_status),
    rg: emptyToUndef(extras.rg),
    issuing_agency: emptyToUndef(extras.issuing_agency),
    trade_name: emptyToUndef(extras.trade_name),
    corporate_name: emptyToUndef(extras.corporate_name),
    address: emptyToUndef(extras.address),
    address_fields: extras.address_fields,
    monthly_income: extras.monthly_income,
    income_proof: emptyToUndef(extras.income_proof),
    notes: metadata.notes ?? '',
    registration_date: metadata.registration_date ?? '',
    lead_origin: parseLeadOrigin(demandProfile),
    lead_interactions: parseLeadInteractions(demandProfile),
    contacts: contacts.map((ct: ClientContact) => ({ type: ct.type, value: ct.value, label: ct.label, handle: ct.handle, source: ct.source })),
  };
}

/**
 * Patches `row` into `prev` when a client with the same id is already
 * there; otherwise, when `canInsert(row)`, prepends it as a brand-new row.
 *
 * @param row - The freshly fetched client row.
 * @param setRows - The rows store setter (accepts a functional updater).
 * @param canInsert - Whether inserting this not-yet-present client is safe
 *   right now (see `canInsertRow` -- inserting under an active filter the
 *   row doesn't match would show a client that shouldn't be in this view).
 * @returns True when `row` was inserted rather than patched.
 */
export function applyClientRow(
  row: ClientRow,
  setRows: (rows: ClientRow[] | ((prev: ClientRow[]) => ClientRow[])) => void,
  canInsert: (row: ClientRow) => boolean,
): boolean {
  let inserted = false;
  setRows((prev) => {
    if (prev.some((r) => r.client_id === row.client_id)) {
      return prev.map((r) => (r.client_id === row.client_id ? row : r));
    }
    if (!canInsert(row)) return prev;
    inserted = true;
    return [row, ...prev];
  });
  return inserted;
}

/**
 * Refetches one client and applies it to the current rows, e.g. on a
 * real-time push -- inserting a not-yet-loaded client (new lead, or one
 * outside the current page) only when `canInsert` accepts the fetched row,
 * and keeping `totalCount` in sync when it does.
 *
 * @param agencyId - The agency the client belongs to.
 * @param clientId - The client to refresh.
 * @param setRows - The rows store setter (accepts a functional updater).
 * @param canInsert - Whether inserting this not-yet-present client is safe right now.
 * @param totalCount - The current reported total, if known.
 * @param setTotalCount - Setter to bump the total when a row is actually inserted.
 */
export function upsertClientRow(
  agencyId: string,
  clientId: string,
  setRows: (rows: ClientRow[] | ((prev: ClientRow[]) => ClientRow[])) => void,
  canInsert: (row: ClientRow) => boolean,
  totalCount: number | null,
  setTotalCount: (n: number | null) => void,
): Promise<void> {
  return backendClientsCrud(agencyId)
    .getById(clientId)
    .then((raw) => {
      const row = toClientRow(raw);
      const inserted = applyClientRow(row, setRows, canInsert);
      if (inserted && totalCount != null) setTotalCount(totalCount + 1);
    })
    .catch(() => {
      /* transient fetch error: leave the existing rows as-is */
    });
}

/** The profile filter keys, for the refetch key and the "any active" check. */
export const CLIENT_PROFILE_FILTER_KEYS: Array<keyof ClientFilters> = [
  'incomeMin', 'incomeMax', 'city', 'neighborhood', 'state',
  'prefCity', 'prefNeighborhood', 'prefPriceMin', 'prefPriceMax',
  'prefBusinessType', 'prefPropertyKind', 'prefBedroomsMin',
  'prefPetFriendly', 'prefFurnished',
];

/**
 * The URL parameter keys the filter panel reads.
 */
const FILTER_KEYS: (keyof ClientFilters)[] = [
  'q', 'type', 'status', 'role', 'broker', 'withoutReturn', 'importId', 'imported',
  ...CLIENT_PROFILE_FILTER_KEYS,
];
const NON_STATUS_FILTER_KEYS = FILTER_KEYS.filter((key) => key !== 'status');

/**
 * Whether a not-yet-loaded client is safe to insert into the list live,
 * given the currently active filters.
 *
 * Exact for `status`: the fetched row's own status decides it, so a new
 * lead pushed while viewing the "Leads" view (status filter) still inserts
 * live instead of only patching rows already on screen. Any other active
 * filter (search text, type, role, broker, sem retorno) has no cheap
 * client-side way to verify a match against the fetched row, so insertion
 * is skipped for those -- left for the next manual reload/filter change.
 *
 * @param row - The freshly fetched client row.
 * @param filters - The current query, type, status, role, and broker filters.
 * @param defaults - The filters' default values.
 * @returns True when it's safe to insert `row` under the active filters.
 */
export function canInsertRow(row: ClientRow, filters: ClientFilters, defaults: ClientFilters): boolean {
  const otherFiltersAtDefault = NON_STATUS_FILTER_KEYS.every((key) => filters[key] === defaults[key]);
  if (!otherFiltersAtDefault) return false;
  return filters.status === defaults.status || row.status === filters.status;
}

/**
 * Real-time updates: a matching push (new/updated Social interaction for
 * this agency) refreshes just that one row instead of waiting for the next
 * manual reload -- see useSocialWebSocket, mounted once in AgencyDashboardLayout.
 * A client not yet in `rows` is inserted too, when `canInsertRow` accepts it
 * under the currently active filters (see its own docstring).
 *
 * @param agencyId - The active agency identifier, if resolved.
 * @param setRows - The rows store setter.
 * @param filters - The current query, type, status, role, and broker filters.
 * @param defaults - The filters' default values.
 * @param totalCount - The current reported total, if known.
 * @param setTotalCount - Setter to bump the total when a row is inserted.
 */
function useSocialRowSync(
  agencyId: string | undefined,
  setRows: (rows: ClientRow[] | ((prev: ClientRow[]) => ClientRow[])) => void,
  filters: ClientFilters,
  defaults: ClientFilters,
  totalCount: number | null,
  setTotalCount: (n: number | null) => void,
): void {
  const lastSocialUpdate = useSocialUpdates((s) => s.lastUpdate);
  useEffect(() => {
    if (!agencyId || !lastSocialUpdate || lastSocialUpdate.agency_id !== agencyId) return;
    const canInsert = (row: ClientRow) => canInsertRow(row, filters, defaults);
    upsertClientRow(agencyId, lastSocialUpdate.client_id, setRows, canInsert, totalCount, setTotalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSocialUpdate, agencyId, setRows, filters, defaults, totalCount, setTotalCount]);
}

/**
 * Builds the backend search-filter map from the active UI filters.
 *
 * @param filters - The current query, type, and status filters.
 * @returns A map of non-empty filter parameters.
 */
function buildSearchFilters(filters: ClientFilters): Record<string, string> {
  return Object.fromEntries(
    Object.entries({
      search: filters.q,
      type: filters.type,
      status: filters.status,
      role: filters.role,
      broker: filters.broker,
      // Server-side, and read from the agency's attendances rather than
      // from the client record: the overview's "clientes sem retorno" tile
      // is what asks for it. camelCase keys throughout, matching
      // backendClientsCrud's Filters type -- a key outside it (snake_case,
      // or simply unlisted) is silently dropped before the request is built.
      withoutReturn: filters.withoutReturn ? 'true' : '',
      importId: filters.importId ?? '',
      // Spreadsheet-imported clients all carry this channel (see
      // SPREADSHEET_IMPORT_CHANNEL in the backend's origin model); narrower
      // than importId (one specific import session), this answers
      // "imported via spreadsheet at all".
      originChannel: filters.imported ? 'spreadsheet_import' : '',
      ...profileSearchFilters(filters),
      // Absent means the backend's own default, which is newest first; only
      // the opposite direction has to be asked for.
      order: filters.order === 'asc' ? 'asc' : '',
    }).filter(([, value]) => Boolean(value)),
  );
}

/**
 * The income, address and preference narrowings, as query-ready strings.
 *
 * Numbers travel as strings because every filter does: the layer below
 * drops any key whose value is falsy, and `0` is not a window a broker can
 * set from this panel.
 *
 * @param filters - The active client-list filters.
 * @returns The subset of query keys these three sections own.
 */
function profileSearchFilters(filters: ClientFilters): Record<string, string> {
  return {
    incomeMin: filters.incomeMin ?? '',
    incomeMax: filters.incomeMax ?? '',
    city: filters.city ?? '',
    neighborhood: filters.neighborhood ?? '',
    state: filters.state ?? '',
    prefCity: filters.prefCity ?? '',
    prefNeighborhood: filters.prefNeighborhood ?? '',
    prefPriceMin: filters.prefPriceMin ?? '',
    prefPriceMax: filters.prefPriceMax ?? '',
    prefBusinessType: filters.prefBusinessType ?? '',
    prefPropertyKind: filters.prefPropertyKind ?? '',
    prefBedroomsMin: filters.prefBedroomsMin ?? '',
    prefPetFriendly: filters.prefPetFriendly ?? '',
    prefFurnished: filters.prefFurnished ?? '',
  };
}

export type ClientFilters = {
  q: string; type: string; status: string; role: string; broker: string;
  withoutReturn?: boolean;
  /**
   * Narrows the list to one spreadsheet import, for the "Ver só estes" action
   * on the post-import strip. Server-side: the rows of an import are not
   * knowable from the client record alone.
   */
  importId?: string;
  /** Narrows to clients created via spreadsheet import, any session. */
  imported?: boolean;
  /** Monthly-income window, in BRL, as typed. */
  incomeMin?: string;
  incomeMax?: string;
  /** Where the client LIVES. */
  city?: string;
  neighborhood?: string;
  state?: string;
  /**
   * What the client is LOOKING FOR, read from the demand profile. Kept
   * apart from the address keys above: living in Sorocaba and hunting in
   * Sorocaba are different questions, and a broker asking the second one
   * does not want the answer to the first.
   */
  prefCity?: string;
  prefNeighborhood?: string;
  prefPriceMin?: string;
  prefPriceMax?: string;
  prefBusinessType?: string;
  prefPropertyKind?: string;
  prefBedroomsMin?: string;
  prefPetFriendly?: string;
  prefFurnished?: string;
  /** `desc` (newest first, the default) or `asc`. */
  order?: string;
};

type FetchSettings = { pageSize: number; page?: number };

/**
 * Fetches and normalizes one page of client rows for an agency.
 *
 * @param agencyId - The agency identifier.
 * @param filters - The active query, type, and status filters.
 * @param fetchSettings - The current page/pageSize to request.
 * @returns The normalized rows for this page, plus the backend's reported page count and total.
 */
async function fetchClientRows(
  agencyId: string,
  filters: ClientFilters,
  fetchSettings: FetchSettings,
): Promise<{ rows: ClientRow[]; pages: number; totalCount: number }> {
  const result = await backendClientsCrud(agencyId).search({
    pagination: { page: fetchSettings.page ?? 1, limit: fetchSettings.pageSize },
    filters: buildSearchFilters(filters),
  });
  const rows = Object.values(result.data).map(toClientRow);
  return { rows, pages: result.metadata.pages, totalCount: result.metadata.totalCount };
}

/**
 * Wires an IntersectionObserver to the sentinel for infinite scrolling.
 *
 * No cap on consecutive auto-loads: reuses the same gate as the properties
 * list so scrolling keeps paginating until the whole result set has loaded,
 * while still guarding the single synchronous instant right after a request
 * is triggered (see `shared/utils/infinite-scroll-gate`).
 *
 * @param sentinelRef - Ref to the sentinel element at the list's end.
 * @param scrollContainerRef - Ref to the scrollable container (observer root).
 * @param isLoading - Whether the initial/filtered fetch is in progress.
 * @param isLoadingMore - Whether a "load more" fetch is in progress.
 * @param totalPages - The backend-reported total page count.
 * @param fetchSettings - The current pagination settings.
 * @param setFetchSettings - Setter to update pagination settings.
 */
function useInfiniteSentinel(
  sentinelRef: React.RefObject<HTMLDivElement | null>,
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
  isLoading: boolean,
  isLoadingMore: boolean,
  totalPages: number,
  fetchSettings: FetchSettings,
  setFetchSettings: (s: FetchSettings) => void,
) {
  const gateRef = useRef(createInfiniteScrollGate());
  const page = fetchSettings.page ?? 1;

  useEffect(() => {
    if (page === 1) resetInfiniteScrollGate(gateRef.current);
  }, [page]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const idle = !isLoading && !isLoadingMore;
        const hasMore = page < totalPages;
        if (!shouldRequestNextPage(entry.isIntersecting, idle, hasMore, gateRef.current)) return;
        markNextPageRequested(gateRef.current);
        setFetchSettings({ ...fetchSettings, page: page + 1 });
      },
      { root: scrollContainerRef.current, threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isLoading, isLoadingMore, totalPages, fetchSettings, setFetchSettings, sentinelRef, scrollContainerRef, page]);
}

interface FetchClientsArgs {
  agencyId?: string;
  filters: ClientFilters;
  fetchSettings: FetchSettings;
  isLoadMore: boolean;
  setRows: (rows: ClientRow[] | ((prev: ClientRow[]) => ClientRow[])) => void;
  setIsLoading: (v: boolean) => void;
  setIsLoadingMore: (v: boolean) => void;
  setLoadError: (v: boolean) => void;
  setTotalPages: (n: number) => void;
  setTotalCount: (n: number | null) => void;
}

/**
 * Sets the loading flags a fetch starts with -- load-more never resets the error banner.
 */
function beginFetchClients(args: FetchClientsArgs): void {
  if (args.isLoadMore) {
    args.setIsLoadingMore(true);
  } else {
    args.setIsLoading(true);
    args.setLoadError(false);
  }
}

/**
 * A load-more failure leaves the existing rows and error state alone; only an
 * initial-load failure shows the error banner.
 */
function handleFetchClientsError(args: FetchClientsArgs): void {
  if (!args.isLoadMore) args.setLoadError(true);
}

/**
 * Clears whichever loading flag the fetch started with.
 */
function finishFetchClients(args: FetchClientsArgs): void {
  if (args.isLoadMore) args.setIsLoadingMore(false);
  else args.setIsLoading(false);
}

/**
 * Loads one page of client rows into the store, tracking loading and error
 * state. Appends to the existing rows when `isLoadMore` is set, otherwise
 * replaces them (initial load or a filter change resetting to page 1).
 *
 * @param args - The agency, filters, pagination, and store setters.
 * @returns A promise that resolves once the fetch settles.
 */
async function runFetchClients(args: FetchClientsArgs): Promise<void> {
  if (!args.agencyId) return;
  beginFetchClients(args);
  try {
    const { rows, pages, totalCount } = await fetchClientRows(args.agencyId, args.filters, args.fetchSettings);
    args.setTotalPages(pages);
    args.setTotalCount(totalCount);
    args.setRows((prev) => (args.isLoadMore ? [...prev, ...rows] : rows));
  } catch {
    handleFetchClientsError(args);
  } finally {
    finishFetchClients(args);
  }
}

/**
 * When filters changed while parked on a page past the first, resets
 * pagination back to page 1 instead of fetching with stale settings.
 *
 * @param filterChanged - Whether the filter key differs from the previous render.
 * @param fetchSettings - The current pagination settings.
 * @param setFetchSettings - Setter to update pagination settings.
 * @returns Whether a reset was triggered (callers should skip fetching this render).
 */
function resetFiltersToFirstPage(
  filterChanged: boolean,
  fetchSettings: FetchSettings,
  setFetchSettings: (s: FetchSettings) => void,
): boolean {
  const page = fetchSettings.page ?? 1;
  if (!filterChanged || page <= 1) return false;
  setFetchSettings({ ...fetchSettings, page: 1 });
  return true;
}

/**
 * Manages the client list: paginated fetching, URL-synced filters, and
 * infinite scroll (uncapped, matching the properties list).
 *
 * @param agencyId - The active agency identifier, if resolved.
 * @returns Rows, loading/error state, totals, filters, and scroll refs for the list UI.
 */
export function useClientList(agencyId: string | undefined) {
  const setRows = useClientCrudStore((s) => s.setRows);
  const setIsLoading = useClientCrudStore((s) => s.setIsLoading);
  const fetchId = useClientCrudStore((s) => s.fetchId);
  const refreshFetch = useClientCrudStore((s) => s.refreshFetch);
  const isLoading = useClientCrudStore((s) => s.isLoading);
  const rows = useClientCrudStore((s) => s.rows);
  const fetchSettings = useClientCrudStore((s) => s.fetchSettings);
  const setFetchSettings = useClientCrudStore((s) => s.setFetchSettings);
  const totalPages = useClientCrudStore((s) => s.totalPages);
  const setTotalPages = useClientCrudStore((s) => s.setTotalPages);
  const totalCount = useClientCrudStore((s) => s.totalCount);
  const setTotalCount = useClientCrudStore((s) => s.setTotalCount);

  const [loadError, setLoadError] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Typed as ClientFilters rather than inferred: the profile keys arrive
  // through a spread, which widens the literal to an index signature and
  // loses every one of them from the inferred shape.
  const DEFAULTS = useMemo<ClientFilters>(
    () => ({
      q: '', type: '', status: '', role: '', broker: '', withoutReturn: false,
      importId: '', imported: false, order: 'desc',
      ...Object.fromEntries(CLIENT_PROFILE_FILTER_KEYS.map((key) => [key, ''])),
    }),
    [],
  );
  const { filters, setFilters } = useUrlFilters(DEFAULTS);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const prevFilterKeyRef = useRef('');

  useEffect(() => {
    if (!agencyId) return;
    const filterKey = [
      fetchId, agencyId, filters.q, filters.type, filters.status, filters.role,
      filters.broker, filters.withoutReturn, filters.importId, filters.imported,
      filters.order, ...CLIENT_PROFILE_FILTER_KEYS.map((key) => filters[key]),
    ].join('|');
    const filterChanged = prevFilterKeyRef.current !== '' && prevFilterKeyRef.current !== filterKey;
    prevFilterKeyRef.current = filterKey;
    const resetToFirstPage = resetFiltersToFirstPage(filterChanged, fetchSettings, setFetchSettings);
    if (resetToFirstPage) return;
    const page = fetchSettings.page ?? 1;
    runFetchClients({
      agencyId, filters, fetchSettings, isLoadMore: page > 1 && !filterChanged,
      setRows, setIsLoading, setIsLoadingMore, setLoadError, setTotalPages, setTotalCount,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchId, agencyId, filters, fetchSettings]);

  useInfiniteSentinel(sentinelRef, scrollContainerRef, isLoading, isLoadingMore, totalPages, fetchSettings, setFetchSettings);
  useSocialRowSync(agencyId, setRows, filters, DEFAULTS, totalCount, setTotalCount);

  return {
    rows, isLoading, isLoadingMore, loadError, totalCount, totalPages,
    fetchClients: refreshFetch,
    filters, setFilters,
    sentinelRef, scrollContainerRef,
  };
}
